import { Badge, Box, Button, Card, Flex, Grid, Heading, HStack, InfoBox, Stack, Text, Textarea } from 'components/ui';
import { useTranslation } from 'hooks/useTranslation';
import { useEffect, useState } from 'react';
import { MdArrowBack, MdCheck, MdContentCopy, MdError, MdShield } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { guardrailsService, PolicyViolationDetail } from 'services/guardrailsService';
import { getThemeColor } from 'theme/colors';

const PolicyViolationDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [violation, setViolation] = useState<PolicyViolationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedMessage, setCopiedMessage] = useState(false);
  const [copiedReason, setCopiedReason] = useState(false);

  const loadViolation = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await guardrailsService.getViolation(id);
      setViolation(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || t('app.violations.detailsPage.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadViolation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const copyToClipboard = async (text: string, type: 'message' | 'reason') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'message') {
        setCopiedMessage(true);
        setTimeout(() => setCopiedMessage(false), 2000);
      } else {
        setCopiedReason(true);
        setTimeout(() => setCopiedReason(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy to clipboard', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card.Root>
        <Card.Body>
          <Text textAlign="center" color={{ base: 'gray.600', _dark: 'white' }}>
            {t('app.violations.detailsPage.loading')}
          </Text>
        </Card.Body>
      </Card.Root>
    );
  }

  if (!violation) {
    return (
      <InfoBox variant="error">
        <Text color="red.700">{error || t('app.violations.detailsPage.notFound')}</Text>
      </InfoBox>
    );
  }

  return (
    <Box>
      <Flex gap={4} mb={6}>
        <Button
          onClick={() => navigate('/admin/policy-violations')}
          variant="ghost"
          colorPalette="gray"
        >
          <MdArrowBack />
          {t('app.violations.detailsPage.backToViolations')}
        </Button>
      </Flex>

      {error && (
        <InfoBox variant="error" mb={4}>
          <Text color="red.700">{error}</Text>
        </InfoBox>
      )}

      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor('card.border')}
        borderRadius="md"
        mb={4}
      >
        <Box borderBottom="1px solid" borderColor={getThemeColor('card.border')}>
          <Card.Header>
            <Flex justify="space-between" align="center">
              <HStack>
                <MdShield size={24} color="red" />
                <Heading size="lg">{t('app.violations.detailsPage.title')}</Heading>
              </HStack>
              <Badge
                colorPalette="red"
                variant="outline"
                fontSize="md"
                px={3}
                py={1}
              >
                <HStack gap={2}>
                  <MdError size={18} />
                  <Text>{t('app.violations.detailsPage.version', { version: violation?.policyVersion })}</Text>
                </HStack>
              </Badge>
            </Flex>
          </Card.Header>
        </Box>

        <Card.Body>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
            {/* Metadata */}
            <Box>
              <Text fontSize="sm" fontWeight="bold" color={{ base: 'gray.500', _dark: 'gray.400' }} mb={1}>
                {t('app.violations.detailsPage.policyVersionLabel')}
              </Text>
              <Text fontSize="sm" mb={4}>
                {violation?.policyVersion}
              </Text>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="bold" color={{ base: 'gray.500', _dark: 'gray.400' }} mb={1}>
                {t('app.violations.detailsPage.timestampLabel')}
              </Text>
              <Text fontSize="sm" mb={4}>
                {violation ? formatDate(violation.timestamp) : t('app.common.notAvailable')}
              </Text>
            </Box>
          </Grid>
        </Card.Body>
      </Card.Root>

      {/* Incoming Message */}
      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor('card.border')}
        borderRadius="md"
        mb={4}
      >
        <Box borderBottom="1px solid" borderColor={getThemeColor('card.border')}>
          <Card.Header>
            <Flex justify="space-between" align="center">
              <Heading size="md">{t('app.violations.detailsPage.incomingMessageTitle')}</Heading>
              <Button
                size="sm"
                variant="ghost"
                colorPalette="blue"
                onClick={() => copyToClipboard(violation?.incomingMessage || '', 'message')}
              >
                {copiedMessage ? (
                  <>
                    <MdCheck /> {t('app.violations.detailsPage.copied')}
                  </>
                ) : (
                  <>
                    <MdContentCopy /> {t('app.violations.detailsPage.copy')}
                  </>
                )}
              </Button>
            </Flex>
          </Card.Header>
        </Box>
        <Card.Body>
          <Textarea
            value={violation?.incomingMessage || ''}
            readOnly
            rows={6}
            fontFamily="mono"
            fontSize="sm"
            bg={getThemeColor('codeBlock.bg')}
            borderColor={getThemeColor('codeBlock.border')}
            color={getThemeColor('codeBlock.text')}
            resize="vertical"
          />
        </Card.Body>
      </Card.Root>

      {/* Violation Reasons */}
      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor('card.border')}
        borderRadius="md"
        mb={4}
      >
        <Box borderBottom="1px solid" borderColor={getThemeColor('card.border')}>
          <Card.Header>
            <Heading size="md">{t('app.violations.detailsPage.violationReasonsTitle')}</Heading>
          </Card.Header>
        </Box>
        <Card.Body>
          <Stack gap={2}>
            {violation?.violationReasons && violation.violationReasons.length > 0 ? (
              violation.violationReasons.map((reason, idx) => (
                <Flex key={idx} align="start" gap={3}>
                  <Box mt={1}>
                    <MdError color="red" size={20} />
                  </Box>
                  <Text fontSize="sm" flex={1}>
                    {reason}
                  </Text>
                </Flex>
              ))
            ) : (
              <Text fontSize="sm" color={{ base: 'gray.500', _dark: 'gray.400' }}>
                {t('app.violations.detailsPage.noViolationReasons')}
              </Text>
            )}
          </Stack>
        </Card.Body>
      </Card.Root>

      {/* Blocking Response */}
      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor('card.border')}
        borderRadius="md"
      >
        <Box borderBottom="1px solid" borderColor={getThemeColor('card.border')}>
          <Card.Header>
            <Flex justify="space-between" align="center">
              <Heading size="md">{t('app.violations.detailsPage.blockingResponseTitle')}</Heading>
              <Button
                size="sm"
                variant="ghost"
                colorPalette="blue"
                onClick={() => copyToClipboard(violation?.blockingResponse || '', 'reason')}
              >
                {copiedReason ? (
                  <>
                    <MdCheck /> {t('app.violations.detailsPage.copied')}
                  </>
                ) : (
                  <>
                    <MdContentCopy /> {t('app.violations.detailsPage.copy')}
                  </>
                )}
              </Button>
            </Flex>
          </Card.Header>
        </Box>
        <Card.Body>
          <Textarea
            value={violation?.blockingResponse || ''}
            readOnly
            rows={4}
            fontFamily="mono"
            fontSize="sm"
            bg={getThemeColor('codeBlock.bg')}
            borderColor={getThemeColor('codeBlock.border')}
            color={getThemeColor('codeBlock.text')}
            resize="vertical"
          />
        </Card.Body>
      </Card.Root>
    </Box>
  );
};

export default PolicyViolationDetails;
